import { memo } from "react";
import classNames from "classnames";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { NestedEditorRow } from "../nestedRowTypes";
import { NestedEditorTableMeta } from "../nestedTableMeta";
import { EditorInlineEditField } from "../../cells/EditorInlineEditField";
import sharedStyles from "../../ChartOfAccountsTemplateEditor.module.scss";

// Code cell: an editable account number for code-editable accounts (custom AND
// Kick built-ins), the placeholder label (behind an info tooltip) for
// placeholder rows, and blank for section/built-in tier/group rows.
export const NestedCodeCell = memo(
    ({
        row,
        table,
    }: CellContext<ViewTableFeatures, NestedEditorRow, unknown>) => {
        const item = row.original;
        const { account } = item;
        const meta = table.options.meta as NestedEditorTableMeta | undefined;

        if (!account) {
            return <ViewTableCell />;
        }

        const placeholder = meta?.getRowCapabilities(account).placeholder;
        if (placeholder) {
            return (
                <ViewTableCell>
                    <Tooltip content={placeholder.tooltip}>
                        <span
                            className={classNames(
                                sharedStyles.staticCellText,
                                sharedStyles.variableText,
                            )}
                        >
                            {placeholder.label}
                        </span>
                    </Tooltip>
                </ViewTableCell>
            );
        }

        const hasDuplicateCode =
            meta?.duplicateCodeIds != null &&
            item.accounts.some((a) => meta.duplicateCodeIds.has(a.id));

        const isLocked = meta?.isEditingDisabled === true;
        return (
            <ViewTableCell captureClicks={!isLocked}>
                <span
                    className={sharedStyles.editorCellAnchor}
                    data-editor-cell={`${item.id}:code`}
                >
                    <EditorInlineEditField
                        value={account.codeText ?? ""}
                        addText="Add code"
                        hasError={hasDuplicateCode}
                        readonly={isLocked}
                        onCommit={(raw) =>
                            meta?.callbacks?.onUpdateAccount(account.id, {
                                codeText: raw,
                            })
                        }
                    />
                </span>
            </ViewTableCell>
        );
    },
);

NestedCodeCell.displayName = "NestedCodeCell";
